import React from "react";
import Heading from "../../components/Heading";
import CheckBoxTable from "../../components/CheckBoxTable";
import RadioTable from "../../components/RadioTable";
import QuestionAnswerBlock from "../../components/QuestionAnswerBlock";
import { Column } from "../../components/BaseTable";

interface Question {
  vraag: string;
  antwoord?: string | null;
  antwoorden?: Record<string, boolean>;
  mogelijkeAntwoorden?: string[];
  toelichting?: string | null;
  // Optionally, allow for section headings in the data
  heading?: string;
}

interface KlantprofielChapterProps {
  title: string;
  questions: Question[];
  radioTableColumns?: Column[];
  radioTableLabelAccessor?: string;
  radioTableValueAccessor?: string;
  reverseOrder?: boolean;
}

const defaultRadioTableColumns: Column[] = [
  { header: "Onderwerp", accessor: "", align: "left" },
  { header: "Geen kennis", accessor: "Geen kennis", align: "center" },
  { header: "Weinig kennis", accessor: "Weinig kennis", align: "center" },
  { header: "Enige kennis", accessor: "Enige kennis", align: "center" },
  { header: "Veel kennis", accessor: "Veel kennis", align: "center" },
];

const KlantprofielChapter: React.FC<KlantprofielChapterProps> = ({
  title,
  questions,
  radioTableColumns = defaultRadioTableColumns,
  radioTableLabelAccessor = "vraag",
  radioTableValueAccessor = "antwoord",
  reverseOrder = false,
}) => {
  // Group questions for RadioTable if they have a "kennis" scale
  const radioTableQuestions = questions.filter(
    (q) =>
      q.mogelijkeAntwoorden &&
      [
        "Geen kennis",
        "Weinig kennis",
        "Enige kennis",
        "Veel kennis",
      ].every((opt) => q.mogelijkeAntwoorden?.includes(opt))
  );
  // The rest are not for RadioTable
  const otherQuestions = questions.filter(
    (q) => !radioTableQuestions.includes(q)
  );

  return (
    <section className="chapter klantprofiel-generic base-chapter">
      {(() => {
        const radioTableBlock =
          radioTableQuestions.length > 0 ? (
            <div className="break-avoid">
              <Heading text="Kennisniveau" level={3} ignoreNumbers ignoreTOC />
              <RadioTable
                columns={radioTableColumns}
                data={radioTableQuestions}
                labelAccessor={radioTableLabelAccessor}
                valueAccessor={radioTableValueAccessor}
                toelichting={radioTableQuestions[0].toelichting || null}
              />
            </div>
          ) : null;

        const otherQuestionsFirstBlock =
          otherQuestions[0] &&
          (otherQuestions[0].heading ? (
            <Heading
              key={`heading-0`}
              text={otherQuestions[0].heading}
              level={3}
              ignoreNumbers
              ignoreTOC
            />
          ) : otherQuestions[0].antwoorden ? (
            <CheckBoxTable
              key={`checkbox-0`}
              data={otherQuestions[0].antwoorden}
              name={`checkbox-0`}
              headingText={otherQuestions[0].vraag}
              toelichting={otherQuestions[0].toelichting}
            />
          ) : (
            <QuestionAnswerBlock
              key={`qab-0`}
              vraag={otherQuestions[0].vraag}
              antwoord={otherQuestions[0].antwoord || null}
              mogelijkeAntwoorden={otherQuestions[0].mogelijkeAntwoorden}
              toelichting={otherQuestions[0].toelichting}
              index={0}
              namePrefix={title.replace(/\s+/g, "-").toLowerCase()}
            />
          ));

        const otherQuestionsBlock = otherQuestions.slice(1).map((q, idx) => {
          const realIdx = idx;
          if (q.heading) {
            return (
              <Heading
                key={`heading-${realIdx}`}
                text={q.heading}
                level={3}
                ignoreNumbers
                ignoreTOC
              />
            );
          }
          if (q.antwoorden) {
            return (
              <CheckBoxTable
                key={`checkbox-${realIdx}`}
                data={q.antwoorden}
                name={`checkbox-${realIdx}`}
                headingText={q.vraag}
                toelichting={q.toelichting}
              />
            );
          }
          return (
            <QuestionAnswerBlock
              key={`qab-${realIdx}`}
              vraag={q.vraag}
              antwoord={q.antwoord || null}
              mogelijkeAntwoorden={q.mogelijkeAntwoorden}
              toelichting={q.toelichting}
              index={realIdx}
              namePrefix={title.replace(/\s+/g, "-").toLowerCase()}
            />
          );
        });

        // Render blocks in the specified order
        if (reverseOrder) {
          return (
            <>
              <div className="break-avoid">
                <Heading text={title} level={2} ignoreNumbers ignoreTOC />
                {otherQuestionsFirstBlock}
              </div>
              {otherQuestionsBlock}
              {radioTableBlock}
            </>
          );
        } else {
          return (
            <>
              <div className="break-avoid">
                <Heading text={title} level={2} ignoreNumbers ignoreTOC />
                {radioTableBlock}
              </div>
              {otherQuestionsFirstBlock}
              {otherQuestionsBlock}
            </>
          );
        }
      })()}
    </section>
  );
};

export default KlantprofielChapter;
